import { useEffect, useState } from "react";
import {
  Building2,
  Palette,
  Save,
  Globe,
  MapPin,
  Users2,
  Trash2,
  Plus,
  Mail,
  Send,
  Key,
  Webhook as WebhookIcon,
  Copy,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";

const INDUSTRIES = [
  "Software & SaaS",
  "Legal & Professional Services",
  "Financial Services",
  "Healthcare",
  "Real Estate",
  "Retail & E-commerce",
  "Manufacturing",
  "Education",
  "Non-profit",
  "Other",
];
const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Madrid",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function OrgDetails({ org, setOrg }) {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/org", {
        name: org.name,
        email_domain: org.email_domain || null,
        phone: org.phone || null,
        timezone: org.timezone,
        industry: org.industry,
        size: org.size,
        address: org.address,
      });
      setOrg(data);
      toast.success("Organization saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="org-details-panel">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-emerald-600" />
        <h3 className="font-semibold">Organization profile</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Company details shown on documents and certificates.
      </p>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Organization name</Label>
          <Input
            value={org.name || ""}
            onChange={(e) => setOrg({ ...org, name: e.target.value })}
            className="mt-1.5 h-10"
            data-testid="org-name-input"
          />
        </div>
        <div>
          <Label>
            <Globe className="h-3 w-3 inline mr-1" /> Email domain
          </Label>
          <Input
            value={org.email_domain || ""}
            onChange={(e) => setOrg({ ...org, email_domain: e.target.value })}
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>Business phone</Label>
          <Input
            value={org.phone || ""}
            onChange={(e) => setOrg({ ...org, phone: e.target.value })}
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>Industry</Label>
          <Select
            value={org.industry || INDUSTRIES[0]}
            onValueChange={(v) => setOrg({ ...org, industry: v })}
          >
            <SelectTrigger className="mt-1.5 h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((i) => (
                <SelectItem key={i} value={i}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Size</Label>
          <Select
            value={org.size || SIZES[0]}
            onValueChange={(v) => setOrg({ ...org, size: v })}
          >
            <SelectTrigger className="mt-1.5 h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIZES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Timezone</Label>
          <Select
            value={org.timezone || "UTC"}
            onValueChange={(v) => setOrg({ ...org, timezone: v })}
          >
            <SelectTrigger className="mt-1.5 h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2 pt-3 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" />
            Registered address
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Street</Label>
          <Input
            value={org.address?.line1 || ""}
            onChange={(e) =>
              setOrg({ ...org, address: { ...org.address, line1: e.target.value } })
            }
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>City</Label>
          <Input
            value={org.address?.city || ""}
            onChange={(e) =>
              setOrg({ ...org, address: { ...org.address, city: e.target.value } })
            }
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>Region / State</Label>
          <Input
            value={org.address?.region || ""}
            onChange={(e) =>
              setOrg({ ...org, address: { ...org.address, region: e.target.value } })
            }
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>Postal code</Label>
          <Input
            value={org.address?.postal_code || ""}
            onChange={(e) =>
              setOrg({
                ...org,
                address: { ...org.address, postal_code: e.target.value },
              })
            }
            className="mt-1.5 h-10"
          />
        </div>
        <div>
          <Label>Country</Label>
          <Input
            value={org.address?.country || ""}
            onChange={(e) =>
              setOrg({ ...org, address: { ...org.address, country: e.target.value } })
            }
            className="mt-1.5 h-10"
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          data-testid="org-save-btn"
        >
          <Save className="h-4 w-4" />
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function Branding({ org, setOrg }) {
  const [busy, setBusy] = useState(false);
  const setBrand = (patch) =>
    setOrg({ ...org, branding: { ...org.branding, ...patch } });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/org/branding", {
        accent_color: org.branding.accent_color,
        tagline: org.branding.tagline,
        signer_heading: org.branding.signer_heading || "",
        signer_body: org.branding.signer_body || "",
        signer_footer: org.branding.signer_footer || "",
        signer_cta_label: org.branding.signer_cta_label || "",
        signer_logo_url: org.branding.signer_logo_url || "",
      });
      setOrg(data);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="branding-panel">
      <div className="flex items-center gap-2">
        <Palette className="h-4 w-4 text-emerald-600" />
        <h3 className="font-semibold">Branding</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Personalize documents and signing pages.
      </p>
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <Label>Accent color</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              type="color"
              value={org.branding?.accent_color || "#059669"}
              onChange={(e) => setBrand({ accent_color: e.target.value })}
              className="h-10 w-14 p-1"
            />
            <Input
              value={org.branding?.accent_color || "#059669"}
              onChange={(e) => setBrand({ accent_color: e.target.value })}
              className="h-10"
            />
          </div>
        </div>
        <div>
          <Label>Tagline</Label>
          <Input
            value={org.branding?.tagline || ""}
            onChange={(e) => setBrand({ tagline: e.target.value })}
            className="mt-1.5 h-10"
            placeholder="Signatures that matter."
          />
        </div>
      </div>

      {/* ---- Signer-page customization ---- */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <h4 className="font-semibold text-sm text-slate-800">
          Signer landing page
        </h4>
        <p className="text-xs text-slate-500 mt-0.5">
          Override the default copy your signers see. Leave any field blank to keep Attestra's default.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <Label>Welcome heading</Label>
            <Input
              value={org.branding?.signer_heading || ""}
              onChange={(e) => setBrand({ signer_heading: e.target.value })}
              className="mt-1.5 h-10"
              placeholder="Please review and sign"
              data-testid="signer-heading-input"
              maxLength={120}
            />
          </div>
          <div>
            <Label>CTA button label</Label>
            <Input
              value={org.branding?.signer_cta_label || ""}
              onChange={(e) => setBrand({ signer_cta_label: e.target.value })}
              className="mt-1.5 h-10"
              placeholder="Finish & sign"
              data-testid="signer-cta-input"
              maxLength={40}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Body / instructions</Label>
            <textarea
              value={org.branding?.signer_body || ""}
              onChange={(e) => setBrand({ signer_body: e.target.value })}
              rows={3}
              className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Thanks for taking the time to review this document. Click below when you're ready to sign."
              data-testid="signer-body-input"
              maxLength={500}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Footer / disclaimer</Label>
            <textarea
              value={org.branding?.signer_footer || ""}
              onChange={(e) => setBrand({ signer_footer: e.target.value })}
              rows={2}
              className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="By signing, you agree to our terms. Contact legal@example.com for questions."
              data-testid="signer-footer-input"
              maxLength={500}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Signer logo URL (optional)</Label>
            <Input
              value={org.branding?.signer_logo_url || ""}
              onChange={(e) => setBrand({ signer_logo_url: e.target.value })}
              className="mt-1.5 h-10"
              placeholder="https://yourcompany.com/logo.png"
              data-testid="signer-logo-url-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Overrides the brand logo just for the signer page. Recommended:
              transparent PNG, height ~40px.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="save-branding-btn"
        >
          {busy ? "Saving…" : "Save branding"}
        </Button>
      </div>

      <TestEmailCard />
    </div>
  );
}

function TestEmailCard() {
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (user?.email && !to) setTo(user.email);
  }, [user, to]);

  const send = async () => {
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/org/test-email", { to: to.trim() || undefined });
      setLastResult({ ok: true, message: `Sent to ${data.to}. Check the inbox.` });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) {
      setLastResult({ ok: false, message: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-slate-200">
      <div className="flex items-start gap-2">
        <Mail className="h-4 w-4 text-emerald-600 mt-1" />
        <div>
          <h4 className="font-semibold text-sm">Test email delivery</h4>
          <p className="text-xs text-slate-500 mt-0.5">
            Send a sample transactional email to verify your sender domain is configured correctly.
          </p>
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3">
        <Input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@yourdomain.com"
          className="h-10"
          data-testid="test-email-input"
        />
        <Button
          onClick={send}
          disabled={busy || !to.trim()}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="send-test-email-btn"
        >
          <Send className="h-4 w-4 mr-1.5" />
          {busy ? "Sending…" : "Send test"}
        </Button>
      </div>
      {lastResult && (
        <div
          className={`mt-3 flex items-start gap-2 text-xs px-3 py-2 rounded-md border ${
            lastResult.ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
          data-testid="test-email-result"
        >
          {lastResult.ok ? (
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          )}
          <span>{lastResult.message}</span>
        </div>
      )}
    </div>
  );
}

function Departments({ canEdit }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/departments");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post("/departments", { name: name.trim(), description: description.trim() || null });
      setName("");
      setDescription("");
      toast.success("Department added");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this department?")) return;
    try {
      await api.delete(`/departments/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="departments-panel">
      <div className="flex items-center gap-2">
        <Users2 className="h-4 w-4 text-emerald-600" />
        <h3 className="font-semibold">Departments</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Group documents and templates by team for cleaner reporting.
      </p>

      {canEdit && (
        <div className="mt-5 grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sales"
              className="mt-1.5 h-10"
              data-testid="dept-name-input"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5 h-10"
              data-testid="dept-desc-input"
            />
          </div>
          <Button
            onClick={create}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="dept-create-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      )}

      <div className="mt-6 divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="text-xs text-slate-500 py-6 text-center">
            No departments yet. {canEdit && "Add your first one above."}
          </div>
        )}
        {items.map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between py-3"
            data-testid={`dept-row-${d.id}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{d.name}</div>
              {d.description && (
                <div className="text-xs text-slate-500 truncate">{d.description}</div>
              )}
            </div>
            {canEdit && (
              <button
                onClick={() => remove(d.id)}
                className="p-2 rounded hover:bg-red-50 text-red-500"
                data-testid={`dept-delete-${d.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApiTokensCard({ canEdit }) {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null); // { id, token }

  const load = async () => {
    try {
      const { data } = await api.get("/api-tokens");
      setTokens(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Give the token a label first.");
    setBusy(true);
    try {
      const { data } = await api.post("/api-tokens", { name: name.trim() });
      setRevealed({ id: data.id, token: data.token });
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this token? Any integration using it will stop working.")) return;
    try {
      await api.delete(`/api-tokens/${id}`);
      toast.success("Token revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6"
      data-testid="api-tokens-panel"
    >
      <div className="flex items-center gap-2">
        <Key className="h-4 w-4 text-emerald-600" />
        <h3 className="font-semibold">API tokens</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Use a token in the <code className="font-mono">Authorization: Bearer …</code> header to
        access Attestra programmatically. Tokens grant the same permissions as the user who
        created them.
      </p>

      {canEdit && (
        <div className="mt-5 grid sm:grid-cols-[1fr_auto] gap-3">
          <Input
            placeholder="Label, e.g. CRM Sync"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-10"
            data-testid="api-token-name-input"
          />
          <Button
            onClick={create}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="api-token-create-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Create token
          </Button>
        </div>
      )}

      <div className="mt-6 divide-y divide-slate-100">
        {tokens.length === 0 && (
          <div className="text-xs text-slate-500 py-6 text-center">
            No API tokens yet.
          </div>
        )}
        {tokens.map((t) => (
          <div
            key={t.id}
            className="flex items-center justify-between gap-2 py-3"
            data-testid={`api-token-row-${t.id}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{t.name}</div>
              <div className="text-[11px] font-mono text-slate-500 truncate">
                {t.prefix}…&nbsp;·&nbsp;created{" "}
                {new Date(t.created_at).toLocaleDateString()}
                {t.last_used_at && (
                  <> · last used {new Date(t.last_used_at).toLocaleDateString()}</>
                )}
                {t.revoked && <span className="ml-2 text-red-600">revoked</span>}
              </div>
            </div>
            {canEdit && !t.revoked && (
              <button
                onClick={() => revoke(t.id)}
                className="p-2 rounded hover:bg-red-50 text-red-500"
                data-testid={`api-token-revoke-${t.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Save this token now</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            For security, this is the only time we'll show the full token. Copy it now and store
            it in your secrets manager.
          </p>
          <div className="mt-2 font-mono text-[11px] break-all bg-slate-50 border border-slate-200 rounded-md p-3">
            {revealed?.token}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(revealed?.token || "");
                toast.success("Copied");
              }}
            >
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button
              onClick={() => setRevealed(null)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="api-token-done"
            >
              I've saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhooksCard({ canEdit }) {
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null); // { id, secret }

  const load = async () => {
    try {
      const [h, e] = await Promise.all([api.get("/webhooks"), api.get("/webhooks/events")]);
      setHooks(h.data);
      setEvents(e.data.events);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleEvent = (ev) => {
    setSelectedEvents((cur) =>
      cur.includes(ev) ? cur.filter((x) => x !== ev) : [...cur, ev],
    );
  };

  const create = async () => {
    if (!url.trim()) return toast.error("Enter an HTTPS endpoint URL.");
    if (selectedEvents.length === 0) return toast.error("Select at least one event.");
    setBusy(true);
    try {
      const { data } = await api.post("/webhooks", {
        url: url.trim(),
        description: description.trim() || null,
        events: selectedEvents,
      });
      setRevealed({ id: data.id, secret: data.secret });
      setUrl("");
      setDescription("");
      setSelectedEvents([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (h) => {
    try {
      await api.patch(`/webhooks/${h.id}`, { active: !h.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this webhook?")) return;
    try {
      await api.delete(`/webhooks/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const test = async (id) => {
    try {
      const { data } = await api.post(`/webhooks/${id}/test`);
      if (data.ok) {
        toast.success(`Delivered (HTTP ${data.status_code}) in ${data.duration_ms}ms`);
      } else {
        toast.error(`Failed: ${data.error || "HTTP " + (data.status_code || "?")}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="mt-6 rounded-xl border border-slate-200 bg-white p-6"
      data-testid="webhooks-panel"
    >
      <div className="flex items-center gap-2">
        <WebhookIcon className="h-4 w-4 text-emerald-600" />
        <h3 className="font-semibold">Webhooks</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Subscribe an HTTPS endpoint to events such as <code>document.completed</code>. Each
        delivery is signed with HMAC-SHA256 using the secret you'll see after creation.
      </p>

      {canEdit && (
        <div className="mt-5 grid gap-3">
          <Input
            placeholder="https://your-app.com/webhooks/attestra"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="h-10"
            data-testid="webhook-url-input"
          />
          <Input
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-10"
            data-testid="webhook-desc-input"
          />
          <div className="flex flex-wrap gap-2">
            {events.map((ev) => {
              const on = selectedEvents.includes(ev);
              return (
                <button
                  key={ev}
                  type="button"
                  onClick={() => toggleEvent(ev)}
                  data-testid={`webhook-event-${ev}`}
                  className={`px-2.5 py-1 text-[11px] font-mono rounded-md border transition ${
                    on
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 hover:bg-slate-50 text-slate-600"
                  }`}
                >
                  {ev}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={create}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="webhook-create-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add webhook
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 divide-y divide-slate-100">
        {hooks.length === 0 && (
          <div className="text-xs text-slate-500 py-6 text-center">
            No webhooks yet.
          </div>
        )}
        {hooks.map((h) => (
          <div key={h.id} className="py-4" data-testid={`webhook-row-${h.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-medium font-mono truncate max-w-[260px] sm:max-w-md">
                    {h.url}
                  </div>
                  {h.active ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      active
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">
                      paused
                    </span>
                  )}
                </div>
                {h.description && (
                  <div className="text-xs text-slate-500 mt-0.5">{h.description}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  {h.events.map((e) => (
                    <span
                      key={e}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-600"
                    >
                      {e}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] font-mono text-slate-400 mt-1.5">
                  secret {h.secret_preview} · created {new Date(h.created_at).toLocaleDateString()}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => test(h.id)}
                    className="p-2 rounded hover:bg-slate-100 text-slate-600"
                    title="Send test"
                    data-testid={`webhook-test-${h.id}`}
                  >
                    <Send className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleActive(h)}
                    className="p-2 rounded hover:bg-slate-100 text-slate-600"
                    title={h.active ? "Pause" : "Resume"}
                    data-testid={`webhook-toggle-${h.id}`}
                  >
                    {h.active ? (
                      <span className="text-[10px] font-semibold tracking-wider px-1.5">
                        PAUSE
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold tracking-wider px-1.5 text-emerald-600">
                        RESUME
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => remove(h.id)}
                    className="p-2 rounded hover:bg-red-50 text-red-500"
                    data-testid={`webhook-delete-${h.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Webhook secret — save it now</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Use this to verify the <code className="font-mono">X-Attestra-Signature</code>{" "}
            header. This is the only time we'll show it in full.
          </p>
          <div className="mt-2 font-mono text-[11px] break-all bg-slate-50 border border-slate-200 rounded-md p-3">
            {revealed?.secret}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(revealed?.secret || "");
                toast.success("Copied");
              }}
            >
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button
              onClick={() => setRevealed(null)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="webhook-secret-done"
            >
              I've saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Snippets Manager (v3.8) ----------
function SnippetsManager({ canEdit }) {
  const [snippets, setSnippets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | snippet
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/snippets");
      setSnippets(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () =>
    setEditing({ name: "", category: "", content: "<p></p>" });

  const startEdit = (s) =>
    setEditing({ id: s.id, name: s.name, category: s.category || "", content: s.content });

  const save = async () => {
    if (!editing?.name?.trim()) return toast.error("Name is required");
    // Strip HTML tags to validate actual content (TipTap empty doc is "<p></p>")
    const plainText = String(editing?.content || "")
      .replace(/<[^>]+>/g, "")
      .trim();
    if (!plainText) return toast.error("Content is required");
    setBusy(true);
    try {
      if (editing.id) {
        await api.patch(`/snippets/${editing.id}`, {
          name: editing.name,
          category: editing.category || null,
          content: editing.content,
        });
      } else {
        await api.post("/snippets", {
          name: editing.name,
          category: editing.category || null,
          content: editing.content,
        });
      }
      toast.success(editing.id ? "Snippet updated" : "Snippet created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/snippets/${s.id}`);
      toast.success("Snippet deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-6"
      data-testid="snippets-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Reusable snippets</h3>
          <p className="text-xs text-slate-500 mt-1">
            Saved rich-text blocks (clauses, boilerplate, headers) that authors can
            insert into any text field with one click.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={startNew}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="new-snippet-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New snippet
          </Button>
        )}
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-slate-500">Loading…</div>
      ) : snippets.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          No snippets yet. Create your first one to speed up document preparation.
        </div>
      ) : (
        <div className="mt-5 grid gap-3">
          {snippets.map((s) => (
            <div
              key={s.id}
              className="rounded-lg border border-slate-200 bg-white p-4 flex gap-4 items-start"
              data-testid={`snippet-row-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  {s.category && (
                    <span className="text-[10px] font-medium uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md px-1.5 py-0.5">
                      {s.category}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-500">
                    used {s.usage_count}×
                  </span>
                </div>
                <div
                  className="mt-1.5 rt-readonly text-xs text-slate-600 line-clamp-3"
                  dangerouslySetInnerHTML={{
                    __html: String(s.content || "").replace(
                      /<\s*script[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi,
                      "",
                    ),
                  }}
                />
              </div>
              {canEdit && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startEdit(s)}
                    data-testid={`edit-snippet-${s.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => remove(s)}
                    data-testid={`delete-snippet-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <SnippetEditDialog
        editing={editing}
        setEditing={setEditing}
        busy={busy}
        onSave={save}
      />
    </div>
  );
}

function SnippetEditDialog({ editing, setEditing, busy, onSave }) {
  if (!editing) return null;
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editing.id ? "Edit snippet" : "New snippet"}
          </DialogTitle>
          <DialogDescription>
            Reusable rich-text block. Insert into any text field via the editor's library icon.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={editing.name || ""}
              onChange={(e) =>
                setEditing({ ...editing, name: e.target.value })
              }
              placeholder="e.g. Confidentiality clause v3"
              className="mt-1.5"
              data-testid="snippet-name-input"
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-xs">Category (optional)</Label>
            <Input
              value={editing.category || ""}
              onChange={(e) =>
                setEditing({ ...editing, category: e.target.value })
              }
              placeholder="Legal / HR / Sales"
              className="mt-1.5"
              data-testid="snippet-category-input"
              maxLength={60}
            />
          </div>
          <div>
            <Label className="text-xs">Content</Label>
            <div
              className="mt-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 min-h-[180px] text-sm"
              data-testid="snippet-content-wrap"
            >
              <RichTextEditor
                value={editing.content || ""}
                onChange={(html) =>
                  setEditing({ ...editing, content: html })
                }
                testId="snippet-content-editor"
                enableSnippets={false}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="snippet-save-btn"
          >
            {busy ? "Saving…" : "Save snippet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org");
        setOrg(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const isAdmin = user?.role === "org_admin" || user?.role === "super_admin";

  if (!org) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
          Settings
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Workspace settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your organization's profile, branding, and defaults.
        </p>
      </div>

      <Tabs defaultValue="organization" className="mt-8">
        <div className="-mx-4 sm:mx-0 overflow-x-auto px-4 sm:px-0">
          <TabsList className="w-max">
            <TabsTrigger value="organization" data-testid="settings-tab-org">
              Organization
            </TabsTrigger>
            <TabsTrigger value="branding" data-testid="settings-tab-branding">
              Branding
            </TabsTrigger>
            <TabsTrigger value="departments" data-testid="settings-tab-departments">
              Departments
            </TabsTrigger>
            <TabsTrigger value="snippets" data-testid="settings-tab-snippets">
              Snippets
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="settings-tab-integrations">
              API & Webhooks
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="organization">
          <div className="mt-4">
            <fieldset disabled={!isAdmin} className={isAdmin ? "" : "opacity-70"}>
              <OrgDetails org={org} setOrg={setOrg} />
              {!isAdmin && (
                <div className="mt-2 text-xs text-slate-500">
                  Only org admins can edit these fields.
                </div>
              )}
            </fieldset>
          </div>
        </TabsContent>
        <TabsContent value="branding">
          <div className="mt-4">
            <fieldset disabled={!isAdmin} className={isAdmin ? "" : "opacity-70"}>
              <Branding org={org} setOrg={setOrg} />
            </fieldset>
          </div>
        </TabsContent>
        <TabsContent value="departments">
          <div className="mt-4">
            <Departments canEdit={isAdmin} />
          </div>
        </TabsContent>
        <TabsContent value="snippets">
          <div className="mt-4">
            <SnippetsManager canEdit={isAdmin} />
          </div>
        </TabsContent>
        <TabsContent value="integrations">
          <div className="mt-4 space-y-0">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4 flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <span className="text-emerald-600">📘</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Integration docs</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Endpoints, event payloads, and copy-paste Node + Python signature
                  verifiers.
                </div>
              </div>
              <a href="/app/integrations/docs">
                <Button variant="outline" size="sm" data-testid="open-api-docs">
                  Open docs
                </Button>
              </a>
            </div>
            <ApiTokensCard canEdit={isAdmin} />
            <WebhooksCard canEdit={isAdmin} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
